
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import { PERMISSION } from '@/permissions';
import AccountingCategoriesModal from '@/components/advanced/AccountingCategoriesModal.vue';
import EndOfSchoolYear from '@/components/advanced/EndOfSchoolYear.vue';
import ServiceCharge from '@/components/advanced/ServiceCharge.vue';
import TaxRatesModal from '@/components/advanced/TaxRatesModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'auth';

@Component<BusinessSettings>({
	components: {
		AccountingCategoriesModal,
		EndOfSchoolYear,
		ServiceCharge,
		TaxRatesModal,
		TextButton,
		ValidationObserver,
		ValidationProvider
	}
})
export default class BusinessSettings extends Vue {
	@Getter('areApp8CreditsEnabled', { namespace }) private areApp8CreditsEnabled!: boolean;
	@Getter('isK12Location', { namespace }) private isK12Location!: boolean;
	@Getter('showReportingCategory', { namespace }) private showReportingCategory!: boolean;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;
	@Watch('restaurant')
	private onRestaurantChange(): void {
		this.setServiceCharge();
	}

	$refs!: { observer: Validate };

	private PERMISSION = PERMISSION;

	// Service charge
	private serviceChargeActivated: boolean = false;
	private serviceCharge: string|null = null;
	private serviceChargeTypeIsDollar: number = 2;

	// Tax rates
	private showTaxRatesModal: boolean = false;

	// Accounting categories
	private showAccountingCategoriesModal: boolean = false;

	// Order Manager Settings
	private menuManagerConfig: MenuManagerConfig = this.restaurant?.menu_manager_config || {} as MenuManagerConfig;

	// Menu configuration settings
	private menuConfiguration: MenuConfiguration = this.restaurant?.menu_configuration || {} as MenuConfiguration;

	/**
	 * Initialize component
	 *
	 * @return {void}
	 */
	private created(): void {
		this.setServiceCharge();
	}

	/**
	 * Set service charge info depending if null or not
	 *
	 * @return {void}
	 */
	private setServiceCharge(): void {
		if (this.restaurant.service_charge) {
			this.serviceChargeActivated = true;
			this.serviceCharge = this.restaurant.service_charge.slice(0, - 1);
			this.serviceChargeTypeIsDollar = this.restaurant.service_charge.slice(this.restaurant.service_charge.length - 1) === '$' ? 0 : 1;
		}
		else {
			this.serviceChargeActivated = false;
			this.serviceCharge = null;
			this.serviceChargeTypeIsDollar = 2;
		}
	}

	private getServiceCharge(): string|null {
		if (this.serviceChargeActivated && this.serviceCharge && (this.serviceChargeTypeIsDollar === 0 || this.serviceChargeTypeIsDollar === 1)) {
			return this.serviceCharge + (this.serviceChargeTypeIsDollar === 0 ? '$' : '%');
		}
		return null;
	}

	/**
	 * Update service charge from event
	 *
	 * @param {any} serviceObject
	 * @return {void}
	 */
	private updateServiceCharge(serviceObject: any): void {
		this.serviceChargeActivated = serviceObject.chargeActivated;
		this.serviceCharge = serviceObject.charge;
		this.serviceChargeTypeIsDollar = serviceObject.type;
	}

	/**
	 * Validate business settings and emit save event to parent component
	 * Is called by Settings.vue when the save button is clicked
	 *
	 * @return {Promise<void>}
	 */
	public async save(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (isValid) {
			let tempServiceCharge = this.getServiceCharge();
			const updatedRestaurant = {
				...this.restaurant,
				service_charge: tempServiceCharge,
				menu_manager_config: this.menuManagerConfig,
				menu_configuration: this.menuConfiguration
			};
			this.$emit('save-business', updatedRestaurant);
		}
	}
}
